import type { FxQualityTier } from "./fxRuntime";

type DensityModeLike = "balanced" | "ultra" | string;

export type FxQualityTierSample = {
  baseTier: FxQualityTier;
  isPlaying: boolean;
  frameMs: number;
  activeSegmentationTargets: number;
  densityMode?: DensityModeLike;
  nowMs?: number;
};

const TIER_ORDER: FxQualityTier[] = ["draft", "balanced", "final"];
const EMERGENCY_FRAME_MS = 42;
const EMERGENCY_SEGMENTED_FRAME_MS = 34;
const DRAFT_HOLD_MS = 1800;

const clampFrameMs = (value: number): number => {
  if (!Number.isFinite(value)) return 16.7;
  return Math.max(4, Math.min(120, value));
};

function rankTier(tier: FxQualityTier): number {
  return TIER_ORDER.indexOf(tier);
}

function stepDownTier(tier: FxQualityTier): FxQualityTier {
  const rank = rankTier(tier);
  return TIER_ORDER[Math.max(0, rank - 1)];
}

function stepUpTier(tier: FxQualityTier, ceiling: FxQualityTier): FxQualityTier {
  const rank = rankTier(tier);
  const ceilingRank = rankTier(ceiling);
  return TIER_ORDER[Math.min(ceilingRank, rank + 1)];
}

function minTier(a: FxQualityTier, b: FxQualityTier): FxQualityTier {
  return rankTier(a) <= rankTier(b) ? a : b;
}

function computePressure(sample: FxQualityTierSample): 0 | 1 | 2 {
  const frameMs = clampFrameMs(sample.frameMs);
  let pressure: 0 | 1 | 2 = 0;

  if (frameMs >= 33) pressure = 2;
  else if (frameMs >= 24) pressure = 1;

  if (sample.densityMode === "ultra") {
    if (frameMs >= 22) pressure = 2;
    else if (frameMs >= 17) pressure = Math.max(pressure, 1) as 1 | 2;
  }

  if (sample.activeSegmentationTargets >= 4) {
    pressure = 2;
  } else if (sample.activeSegmentationTargets >= 2) {
    pressure = Math.max(pressure, 1) as 1 | 2;
  }

  return pressure;
}

export class FxQualityTierController {
  private currentTier: FxQualityTier;
  private lastUpdateMs: number | null = null;
  private pressureAccumMs = 0;
  private recoveryAccumMs = 0;
  private draftHoldUntilMs = 0;

  constructor(initialTier: FxQualityTier) {
    this.currentTier = initialTier;
  }

  getCurrentTier(): FxQualityTier {
    return this.currentTier;
  }

  reset(nextTier: FxQualityTier): void {
    this.currentTier = nextTier;
    this.lastUpdateMs = null;
    this.pressureAccumMs = 0;
    this.recoveryAccumMs = 0;
    this.draftHoldUntilMs = 0;
  }

  update(sample: FxQualityTierSample): FxQualityTier {
    const nowMs = Number.isFinite(sample.nowMs) ? Number(sample.nowMs) : Date.now();
    const dt = this.lastUpdateMs == null ? 16.7 : Math.max(0, Math.min(250, nowMs - this.lastUpdateMs));
    this.lastUpdateMs = nowMs;
    const frameMs = clampFrameMs(sample.frameMs);

    const baseTier = sample.baseTier;
    this.currentTier = minTier(this.currentTier, baseTier);

    if (!sample.isPlaying) {
      this.pressureAccumMs = 0;
      this.recoveryAccumMs += dt;
      if (rankTier(this.currentTier) < rankTier(baseTier) && this.recoveryAccumMs >= 1000) {
        this.currentTier = stepUpTier(this.currentTier, baseTier);
        this.recoveryAccumMs = 0;
      }
      return this.currentTier;
    }

    const hasSegmentationLoad = sample.activeSegmentationTargets > 0;
    const emergencyPressure =
      frameMs >= EMERGENCY_FRAME_MS ||
      (hasSegmentationLoad && frameMs >= EMERGENCY_SEGMENTED_FRAME_MS);

    if (emergencyPressure) {
      this.currentTier = "draft";
      this.pressureAccumMs = 0;
      this.recoveryAccumMs = 0;
      this.draftHoldUntilMs = Math.max(this.draftHoldUntilMs, nowMs + DRAFT_HOLD_MS);
      return this.currentTier;
    }

    const pressure = computePressure(sample);
    if (pressure > 0) {
      this.pressureAccumMs += dt;
      this.recoveryAccumMs = 0;
    } else {
      this.pressureAccumMs = Math.max(0, this.pressureAccumMs - dt * 1.1);
      this.recoveryAccumMs += dt;
    }

    if (pressure === 2 && this.pressureAccumMs >= 320) {
      this.currentTier = stepDownTier(this.currentTier);
      this.pressureAccumMs = 0;
      if (this.currentTier === "draft") {
        this.draftHoldUntilMs = Math.max(this.draftHoldUntilMs, nowMs + DRAFT_HOLD_MS);
      }
      return this.currentTier;
    }

    if (pressure === 1 && this.pressureAccumMs >= 700) {
      this.currentTier = stepDownTier(this.currentTier);
      this.pressureAccumMs = 0;
      if (this.currentTier === "draft") {
        this.draftHoldUntilMs = Math.max(this.draftHoldUntilMs, nowMs + DRAFT_HOLD_MS);
      }
      return this.currentTier;
    }

    const canRecover =
      pressure === 0 &&
      this.recoveryAccumMs >= 2200 &&
      frameMs <= 16.5 &&
      nowMs >= this.draftHoldUntilMs &&
      rankTier(this.currentTier) < rankTier(baseTier);

    if (canRecover) {
      this.currentTier = stepUpTier(this.currentTier, baseTier);
      this.recoveryAccumMs = 0;
      if (this.currentTier !== "draft") this.draftHoldUntilMs = 0;
    }

    return this.currentTier;
  }
}
